import { apiFetch } from "../api/client";
import { tenantPath } from "../utils/tenantContext";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import API from "../config";

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}

function severityClass(value) {
  return `ueba-badge ueba-badge-${String(
    value || "low"
  ).toLowerCase()}`;
}

function riskClass(value) {
  return `ueba-risk ueba-risk-${String(
    value || "low"
  ).toLowerCase()}`;
}

function statusClass(value) {
  return `ueba-status ueba-status-${String(
    value || "open"
  ).replaceAll("_", "-")}`;
}

function UEBAPage() {
  const [selectedRule, setSelectedRule] =
    useState(null);

  const [summary, setSummary] = useState(null);
  const [entities, setEntities] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedEntity, setSelectedEntity] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingAnomalyId, setUpdatingAnomalyId] =
    useState(null);
  const [recalculating, setRecalculating] =
    useState(false);
  const [error, setError] = useState("");

  const loadUEBA = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const [
        summaryResponse,
        entitiesResponse,
        anomaliesResponse,
        rulesResponse,
      ] = await Promise.all([
        apiFetch(tenantPath(`/ueba/summary`)),
        apiFetch(tenantPath(`/ueba/entities?limit=100`)),
        apiFetch(tenantPath(`/ueba/anomalies?limit=100`)),
        apiFetch(tenantPath(`/ueba/rules`)),
      ]);

      const summaryData = await summaryResponse.json();
      const entitiesData = await entitiesResponse.json();
      const anomaliesData = await anomaliesResponse.json();
      const rulesData = await rulesResponse.json();

      if (!summaryResponse.ok) {
        throw new Error(
          summaryData.detail ||
            "Unable to load UEBA summary."
        );
      }

      if (!entitiesResponse.ok) {
        throw new Error(
          entitiesData.detail ||
            "Unable to load UEBA entities."
        );
      }

      if (!anomaliesResponse.ok) {
        throw new Error(
          anomaliesData.detail ||
            "Unable to load UEBA anomalies."
        );
      }

      if (!rulesResponse.ok) {
        throw new Error(
          rulesData.detail ||
            "Unable to load UEBA rules."
        );
      }

      setSummary(summaryData);

      setEntities(
        Array.isArray(entitiesData)
          ? entitiesData
          : []
      );

      setAnomalies(
        Array.isArray(anomaliesData)
          ? anomaliesData
          : []
      );

      setRules(
        Array.isArray(rulesData)
          ? rulesData
          : []
      );
    } catch (loadError) {
      console.error("UEBA load error:", loadError);
      setError(loadError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadUEBA();
  }, [loadUEBA]);

  useEffect(() => {
    const handleTenantChange = () => {
      loadUEBA();
    };

    window.addEventListener(
      "wirelesspress-tenant-change",
      handleTenantChange
    );

    return () => {
      window.removeEventListener(
        "wirelesspress-tenant-change",
        handleTenantChange
      );
    };
  }, [loadUEBA]);

  const openEntity = async (entityId) => {
    try {
      const response = await apiFetch(tenantPath(`/ueba/entities/${entityId}`)
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load UEBA entity."
        );
      }

      setSelectedEntity(data);
    } catch (entityError) {
      console.error(
        "UEBA entity load error:",
        entityError
      );

      window.alert(entityError.message);
    }
  };

  const updateAnomaly = async (
    anomalyId,
    status
  ) => {
    try {
      setUpdatingAnomalyId(anomalyId);

      const response = await apiFetch(`/ueba/anomalies/${anomalyId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status,
            assigned_to:
              localStorage.getItem("username") ||
              "Authenticated Administrator",
            analyst_notes:
              status === "false_positive"
                ? "Reviewed and marked as false positive."
                : "Reviewed by Wireless Press UEBA analyst.",
            actor_name:
              localStorage.getItem("username") ||
              "Authenticated Administrator",
            actor_role: localStorage.getItem("role") || "authenticated_user",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to update UEBA anomaly."
        );
      }

      await loadUEBA(true);

      if (selectedEntity) {
        await openEntity(
          selectedEntity.id
        );
      }
    } catch (updateError) {
      console.error(
        "UEBA anomaly update error:",
        updateError
      );

      window.alert(updateError.message);
    } finally {
      setUpdatingAnomalyId(null);
    }
  };

  const recalculateRisk = async () => {
    try {
      setRecalculating(true);

      const response = await apiFetch(`/ueba/recalculate-risk`,
        {
          method: "POST",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to recalculate UEBA risk."
        );
      }

      await loadUEBA(true);

      if (selectedEntity) {
        await openEntity(
          selectedEntity.id
        );
      }
    } catch (recalculateError) {
      console.error(
        "UEBA recalculation error:",
        recalculateError
      );

      window.alert(recalculateError.message);
    } finally {
      setRecalculating(false);
    }
  };

  const entityMetrics =
    summary?.entity_metrics || {};

  const anomalyMetrics =
    summary?.anomaly_metrics || {};

  const typeBreakdown = useMemo(
    () =>
      Array.isArray(summary?.anomalies_by_type)
        ? summary.anomalies_by_type
        : [],
    [summary]
  );

  const severityBreakdown = useMemo(
    () =>
      Array.isArray(
        summary?.anomalies_by_severity
      )
        ? summary.anomalies_by_severity
        : [],
    [summary]
  );

  if (loading) {
    return (
      <div className="ueba-loading">
        Loading User and Entity Behavior Analytics...
      </div>
    );
  }

  return (
    <div className="ueba-page">
      <section className="ueba-header">
        <div>
          <div className="ueba-eyebrow">
            USER & ENTITY BEHAVIOR ANALYTICS
          </div>

          <h2>UEBA Security Center</h2>

          <p>
            Monitor behavioral baselines, identity and device
            risk, authentication anomalies, and abnormal
            privileged activity.
          </p>
        </div>

        <div className="ueba-header-actions">
          <button
            type="button"
            onClick={recalculateRisk}
            disabled={recalculating}
          >
            {recalculating
              ? "Recalculating..."
              : "Recalculate Risk"}
          </button>

          <button
            type="button"
            onClick={() => loadUEBA(true)}
            disabled={refreshing}
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh UEBA"}
          </button>
        </div>
      </section>

      {error && (
        <div className="ueba-warning">
          UEBA refresh failed: {error}
        </div>
      )}

      <section className="ueba-metric-grid">
        <article>
          <span>Total Entities</span>
          <strong>
            {entityMetrics.total_entities || 0}
          </strong>
        </article>

        <article>
          <span>User Entities</span>
          <strong>
            {entityMetrics.user_entities || 0}
          </strong>
        </article>

        <article>
          <span>Device Entities</span>
          <strong>
            {entityMetrics.device_entities || 0}
          </strong>
        </article>

        <article className="ueba-high-card">
          <span>High-Risk Entities</span>
          <strong>
            {entityMetrics.high_risk_entities || 0}
          </strong>
        </article>

        <article className="ueba-critical-card">
          <span>Critical Entities</span>
          <strong>
            {entityMetrics.critical_entities || 0}
          </strong>
        </article>

        <article>
          <span>Open Anomalies</span>
          <strong>
            {anomalyMetrics.open_anomalies || 0}
          </strong>
        </article>

        <article>
          <span>Active Baselines</span>
          <strong>
            {entityMetrics.active_baselines || 0}
          </strong>
        </article>

        <article>
          <span>Average Entity Risk</span>
          <strong>
            {Number(
              entityMetrics.average_entity_risk || 0
            ).toFixed(1)}
          </strong>
        </article>
      </section>

      <section className="ueba-grid">
        <article className="ueba-panel">
          <div className="ueba-panel-heading">
            <div>
              <span>ANOMALY DISTRIBUTION</span>
              <h3>By Severity</h3>
            </div>
          </div>

          <div className="ueba-breakdown-list">
            {severityBreakdown.length > 0 ? (
              severityBreakdown.map((item) => (
                <div
                  className="ueba-breakdown-row"
                  key={item.severity}
                >
                  <span
                    className={severityClass(
                      item.severity
                    )}
                  >
                    {formatLabel(item.severity)}
                  </span>

                  <div className="ueba-breakdown-bar">
                    <div
                      style={{
                        width: `${Math.min(
                          100,
                          Number(item.count || 0) * 20
                        )}%`,
                      }}
                    />
                  </div>

                  <strong>{item.count}</strong>
                </div>
              ))
            ) : (
              <div className="ueba-empty">
                No anomaly severity data.
              </div>
            )}
          </div>
        </article>

        <article className="ueba-panel">
          <div className="ueba-panel-heading">
            <div>
              <span>DETECTION COVERAGE</span>
              <h3>Anomaly Types</h3>
            </div>
          </div>

          <div className="ueba-type-grid">
            {typeBreakdown.length > 0 ? (
              typeBreakdown.map((item) => (
                <div key={item.anomaly_type}>
                  <span>
                    {formatLabel(
                      item.anomaly_type
                    )}
                  </span>

                  <strong>{item.count}</strong>
                </div>
              ))
            ) : (
              <div className="ueba-empty">
                No anomaly type data.
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="ueba-panel">
        <div className="ueba-panel-heading">
          <div>
            <span>ENTITY RISK INVENTORY</span>
            <h3>Users and Devices</h3>
          </div>
        </div>

        <div className="ueba-table-wrapper">
          <table className="ueba-table">
            <thead>
              <tr>
                <th>Entity</th>
                <th>Type</th>
                <th>Baseline</th>
                <th>Risk</th>
                <th>Baselines</th>
                <th>Open Anomalies</th>
                <th>Last Activity</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {entities.length > 0 ? (
                entities.map((entity) => (
                  <tr key={entity.id}>
                    <td>
                      <strong>
                        {entity.display_name ||
                          entity.entity_identifier}
                      </strong>

                      <span className="ueba-cell-subtext">
                        {entity.entity_identifier}
                      </span>
                    </td>

                    <td>
                      {formatLabel(
                        entity.entity_type
                      )}
                    </td>

                    <td>
                      {formatLabel(
                        entity.baseline_status
                      )}
                    </td>

                    <td>
                      <span
                        className={riskClass(
                          entity.risk_level
                        )}
                      >
                        {Number(
                          entity.risk_score || 0
                        ).toFixed(1)}
                        {" · "}
                        {formatLabel(
                          entity.risk_level
                        )}
                      </span>
                    </td>

                    <td>
                      {entity.baseline_count || 0}
                    </td>

                    <td>
                      {entity.open_anomaly_count || 0}
                    </td>

                    <td>
                      {formatDate(
                        entity.last_activity_at
                      )}
                    </td>

                    <td>
                      <button
                        type="button"
                        className="ueba-row-button"
                        onClick={() =>
                          openEntity(entity.id)
                        }
                      >
                        View Entity
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="ueba-empty"
                  >
                    No UEBA entities found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ueba-panel">
        <div className="ueba-panel-heading">
          <div>
            <span>ANALYST QUEUE</span>
            <h3>Behavioral Anomalies</h3>
          </div>
        </div>

        <div className="ueba-table-wrapper">
          <table className="ueba-table">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Entity</th>
                <th>Anomaly</th>
                <th>Score</th>
                <th>Confidence</th>
                <th>Status</th>
                <th>First Seen</th>
                <th>Analyst Action</th>
              </tr>
            </thead>

            <tbody>
              {anomalies.length > 0 ? (
                anomalies.map((anomaly) => (
                  <tr key={anomaly.id}>
                    <td>
                      <span
                        className={severityClass(
                          anomaly.severity
                        )}
                      >
                        {formatLabel(
                          anomaly.severity
                        )}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {anomaly.display_name ||
                          anomaly.entity_identifier}
                      </strong>

                      <span className="ueba-cell-subtext">
                        {formatLabel(
                          anomaly.entity_type
                        )}
                      </span>
                    </td>

                    <td>
                      <strong>{anomaly.title}</strong>

                      <span className="ueba-cell-subtext">
                        {formatLabel(
                          anomaly.anomaly_type
                        )}
                      </span>
                    </td>

                    <td>
                      {Number(
                        anomaly.anomaly_score || 0
                      ).toFixed(1)}
                    </td>

                    <td>
                      {Number(
                        anomaly.confidence_score || 0
                      ).toFixed(1)}
                      %
                    </td>

                    <td>
                      <span
                        className={statusClass(
                          anomaly.status
                        )}
                      >
                        {formatLabel(
                          anomaly.status
                        )}
                      </span>
                    </td>

                    <td>
                      {formatDate(
                        anomaly.first_seen_at
                      )}
                    </td>

                    <td>
                      <select
                        value={anomaly.status}
                        disabled={
                          updatingAnomalyId === anomaly.id
                        }
                        onChange={(event) =>
                          updateAnomaly(
                            anomaly.id,
                            event.target.value
                          )
                        }
                      >
                        <option value="open">
                          Open
                        </option>

                        <option value="under_review">
                          Under Review
                        </option>

                        <option value="closed">
                          Closed
                        </option>

                        <option value="false_positive">
                          False Positive
                        </option>
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="ueba-empty"
                  >
                    No UEBA anomalies found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedEntity && (
        <section className="ueba-panel">
          <div className="ueba-panel-heading ueba-detail-heading">
            <div>
              <span>ENTITY INVESTIGATION</span>

              <h3>
                {selectedEntity.display_name ||
                  selectedEntity.entity_identifier}
              </h3>
            </div>

            <button
              type="button"
              className="ueba-close-button"
              onClick={() =>
                setSelectedEntity(null)
              }
            >
              Close
            </button>
          </div>

          <div className="ueba-detail-metrics">
            <div>
              <span>Entity Type</span>
              <strong>
                {formatLabel(
                  selectedEntity.entity_type
                )}
              </strong>
            </div>

            <div>
              <span>Risk</span>
              <strong>
                {Number(
                  selectedEntity.risk_score || 0
                ).toFixed(1)}
                {" · "}
                {formatLabel(
                  selectedEntity.risk_level
                )}
              </strong>
            </div>

            <div>
              <span>Baseline</span>
              <strong>
                {formatLabel(
                  selectedEntity.baseline_status
                )}
              </strong>
            </div>

            <div>
              <span>Anomalies</span>
              <strong>
                {(selectedEntity.anomalies || []).length}
              </strong>
            </div>
          </div>

          <div className="ueba-detail-grid">
            <article>
              <h4>Behavior Baselines</h4>

              {(selectedEntity.baselines || []).map(
                (baseline) => (
                  <div
                    className="ueba-detail-row"
                    key={baseline.id}
                  >
                    <div>
                      <strong>
                        {formatLabel(
                          baseline.behavior_type
                        )}
                      </strong>

                      <span>
                        {baseline.observation_count}
                        {" "}observations
                      </span>
                    </div>

                    <div>
                      <strong>
                        {Number(
                          baseline.confidence_score ||
                            0
                        ).toFixed(1)}
                        %
                      </strong>

                      <span>Confidence</span>
                    </div>
                  </div>
                )
              )}
            </article>

            <article>
              <h4>Risk History</h4>

              {(selectedEntity.risk_history || [])
                .slice(0, 10)
                .map((history) => (
                  <div
                    className="ueba-detail-row"
                    key={history.id}
                  >
                    <div>
                      <strong>
                        {history.risk_score}
                        {" · "}
                        {formatLabel(
                          history.risk_level
                        )}
                      </strong>

                      <span>{history.reason}</span>
                    </div>

                    <div>
                      <span>
                        {formatDate(
                          history.calculated_at
                        )}
                      </span>
                    </div>
                  </div>
                ))}
            </article>
          </div>
        </section>
      )}

      <section className="ueba-panel">
        <div className="ueba-panel-heading">
          <div>
            <span>DETECTION RULES</span>
            <h3>Behavior Analytics Coverage</h3>
          </div>
        </div>

        <div className="ueba-rule-grid">
          {rules.map((rule) => (
            <article key={rule.id}
                role="button"
                tabIndex="0"
                onClick={() => setSelectedRule(rule)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    event.key === " "
                  ) {
                    setSelectedRule(rule);
                  }
                }}
              >
              <div>
                <strong>{rule.rule_name}</strong>
                <span>{rule.rule_key}</span>
              </div>

              <span
                className={severityClass(
                  rule.severity
                )}
              >
                {formatLabel(rule.severity)}
              </span>

              <small>
                {formatLabel(rule.anomaly_type)}
                {" · "}
                Threshold {rule.threshold_score}
                {" · "}
                {rule.create_incident
                  ? "Creates incident"
                  : "Analytics only"}
              </small>
            </article>
          ))}
        </div>
      </section>
      {selectedRule && (
        <section className="ueba-panel ueba-rule-detail-panel">
          <div className="ueba-panel-heading ueba-detail-heading">
            <div>
              <span>DETECTION RULE</span>
              <h3>{selectedRule.rule_name}</h3>
            </div>

            <button
              type="button"
              className="ueba-close-button"
              onClick={() => setSelectedRule(null)}
            >
              Close
            </button>
          </div>

          <p>
            {selectedRule.description ||
              "No rule description supplied."}
          </p>

          <div className="ueba-detail-grid">
            <div className="ueba-detail-row">
              <span>Rule Key</span>
              <strong>{selectedRule.rule_key}</strong>
            </div>

            <div className="ueba-detail-row">
              <span>Status</span>
              <strong>
                {selectedRule.enabled
                  ? "Enabled"
                  : "Disabled"}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>Entity</span>
              <strong>
                {formatLabel(
                  selectedRule.entity_type
                )}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>Behavior</span>
              <strong>
                {formatLabel(
                  selectedRule.behavior_type
                )}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>Anomaly</span>
              <strong>
                {formatLabel(
                  selectedRule.anomaly_type
                )}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>Severity</span>
              <strong>
                {formatLabel(
                  selectedRule.severity
                )}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>Threshold</span>
              <strong>
                {selectedRule.threshold_score}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>Create Incident</span>
              <strong>
                {selectedRule.create_incident
                  ? "Yes"
                  : "No"}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>MITRE Tactic</span>
              <strong>
                {selectedRule.mitre_tactic || "—"}
              </strong>
            </div>

            <div className="ueba-detail-row">
              <span>MITRE Technique</span>
              <strong>
                {selectedRule.mitre_technique || "—"}
              </strong>
            </div>
          </div>

          <div className="ueba-detail-row">
            <span>Configuration</span>
            <pre>
              {JSON.stringify(
                selectedRule.configuration || {},
                null,
                2
              )}
            </pre>
          </div>

          {selectedRule.rule_key ===
            "endpoint-process-spike" && (
            <div className="ueba-warning">
              This rule remains intentionally disabled
              until endpoint process telemetry is connected
              to UEBA observations.
            </div>
          )}
        </section>
      )}

    </div>
  );
}

export default UEBAPage;
